import React, { useEffect, useRef, useState } from "react";
import { toast } from "../../utils/toast";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { createPost, uploadSingleFile } from "../../services/api";
import { FileMetadataDto, PostRequestDto } from "../../types/types";
import {
  PhotoIcon,
  VideoCameraIcon,
  FaceSmileIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { VIDEO_UPLOAD_ENABLED } from "../../config/featureFlags";

interface CreatePostSectionProps {
  eventId?: string;
  className?: string;
}

type FieldName = "title" | "description";

const MIN_TITLE_LENGTH = 2;
const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 2000;

const getLoginResponse = () => {
  try {
    return JSON.parse(localStorage.getItem("loginResponse") || "{}");
  } catch {
    return {};
  }
};

export const CreatePostSection: React.FC<CreatePostSectionProps> = ({
  eventId,
  className = "",
}) => {
  const loginResponse = getLoginResponse();
  const username = loginResponse.username;
  const profileImage = loginResponse.profilePicture;
  const firstName: string = loginResponse.firstName || '';
  const lastName: string = loginResponse.lastName || '';

  const getInitials = (first: string, last: string): string => {
    const f = first.trim().charAt(0).toUpperCase();
    const l = last.trim().charAt(0).toUpperCase();
    return f + l || first.trim().charAt(0).toUpperCase() || '?';
  };

  const getAvatarColor = (name: string): string => {
    const colors = [
      { bg: '#EFF6FF', text: '#2563EB' },
      { bg: '#F0FDF4', text: '#16A34A' },
      { bg: '#FDF4FF', text: '#9333EA' },
      { bg: '#FFF7ED', text: '#EA580C' },
      { bg: '#FFF1F2', text: '#E11D48' },
      { bg: '#F0FDFA', text: '#0D9488' },
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return JSON.stringify(colors[Math.abs(hash) % colors.length]);
  };

  const avatarColor = JSON.parse(getAvatarColor(firstName + lastName || username || '?')) as { bg: string; text: string };
  const initials = getInitials(firstName, lastName);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [titleError, setTitleError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [lastFocusedField, setLastFocusedField] = useState<FieldName>("description");

  const titleRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const emojiBoxRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation("dashboard");

  useEffect(() => {
    if (!showEmojiPicker) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (emojiBoxRef.current && !emojiBoxRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showEmojiPicker]);

  const clearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl(null);
    setFileInputKey((prev) => prev + 1);
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setTitleError("");
    setDescriptionError("");
    setShowEmojiPicker(false);
    setLastFocusedField("description");
    clearFile();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const isTitleField = lastFocusedField === "title";
    const textBox = isTitleField ? titleRef.current : descriptionRef.current;
    const currentText = isTitleField ? title : description;
    const maxLength = isTitleField ? MAX_TITLE_LENGTH : MAX_DESCRIPTION_LENGTH;

    const startPosition = textBox?.selectionStart ?? currentText.length;
    const endPosition = textBox?.selectionEnd ?? currentText.length;

    const newText =
      currentText.slice(0, startPosition) + emojiData.emoji + currentText.slice(endPosition);

    if (newText.length > maxLength) {
      setShowEmojiPicker(false);
      return;
    }

    if (isTitleField) {
      setTitle(newText);
      if (newText.trim().length >= MIN_TITLE_LENGTH) setTitleError("");
    } else {
      setDescription(newText);
      setDescriptionError("");
    }

    setShowEmojiPicker(false);

    const cursorPosition = startPosition + emojiData.emoji.length;
    setTimeout(() => {
      textBox?.focus();
      textBox?.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();

    if (trimmedTitle.length < MIN_TITLE_LENGTH) {
      setTitleError(
        trimmedTitle
          ? t("title_min_length", { defaultValue: "Title must be at least 2 characters" })
          : t("title_required")
      );
      return;
    }

    if (!trimmedDescription) {
      setDescriptionError(t("description_required", { defaultValue: "Description is required" }));
      return;
    }

    try {
      setTitleError("");
      setDescriptionError("");
      setLoading(true);

      let uploadedData: FileMetadataDto | null = null;

      if (selectedFile) {
        uploadedData = await uploadSingleFile(selectedFile);
      }

      const dto: PostRequestDto = {
        title: trimmedTitle,
        description: trimmedDescription,
        fileMetadataDto: uploadedData || undefined,
      };

      if (eventId) {
        dto.eventId = eventId;
      }

      await createPost(username, dto);
      toast.success(t("success"));
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        error.message ||
        t("error");
      toast.error(errMsg);
    } finally {
      setLoading(false);
      resetForm();
    }
  };

  return (
    <div className={`w-full min-w-0 ${className}`}>
      <div className="flex w-full min-w-0 flex-col gap-3 rounded-3xl bg-white p-4 font-['Plus_Jakarta_Sans',sans-serif] shadow-sm ring-1 ring-black/5">
        <div className="flex flex-col pr-6">
          <p className="text-left text-[14px] font-medium leading-[20px] text-gray-900">
            {t("new_post", { defaultValue: "New post" })}
          </p>
          <p className="text-left text-[12px] font-normal leading-[16px] text-gray-500">
            {t("share_news", { defaultValue: "Share news with your community" })}
          </p>
        </div>

        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-3xl"
          style={{ backgroundColor: profileImage ? undefined : avatarColor.bg }}
        >
          {profileImage ? (
            <img src={profileImage} alt="Profile" className="h-full w-full object-cover" />
          ) : (
            <span
              className="text-sm font-semibold leading-none select-none"
              style={{ color: avatarColor.text }}
            >
              {initials}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <input
            ref={titleRef}
            type="text"
            placeholder={t("title")}
            value={title}
            maxLength={MAX_TITLE_LENGTH}
            onFocus={() => setLastFocusedField("title")}
            onChange={(e) => {
              setTitle(e.target.value);
              if (e.target.value.trim().length >= MIN_TITLE_LENGTH) setTitleError("");
            }}
            className={`h-10 w-full min-w-0 rounded-xl bg-gray-100 px-3 py-2 text-[14px] font-normal leading-[20px] text-gray-800 outline-none placeholder-gray-400 focus:ring-1 ${
              titleError ? "ring-1 ring-red-300 focus:ring-red-500" : "focus:ring-blue-500"
            }`}
          />
          <div className="flex flex-wrap items-start justify-between gap-x-2">
            <p className="text-left text-[12px] font-normal leading-[16px] text-gray-500">
              {t("characters", { defaultValue: "Characters" })}: {title.length}/{MAX_TITLE_LENGTH}
            </p>
            {titleError && (
              <p className="text-right text-[12px] leading-[16px] text-red-500">{titleError}</p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <textarea
            ref={descriptionRef}
            placeholder={t("description")}
            value={description}
            maxLength={MAX_DESCRIPTION_LENGTH}
            onFocus={() => setLastFocusedField("description")}
            onChange={(e) => {
              setDescription(e.target.value);
              if (e.target.value.trim()) setDescriptionError("");
            }}
            className={`h-[100px] w-full min-w-0 resize-none rounded-xl bg-gray-100 px-3 py-2 text-[14px] font-normal leading-[20px] text-gray-800 outline-none placeholder-gray-400 focus:ring-1 ${
              descriptionError ? "ring-1 ring-red-300 focus:ring-red-500" : "focus:ring-blue-500"
            }`}
          />
          <div className="flex flex-wrap items-start justify-between gap-x-2">
            <p className="text-left text-[12px] font-normal leading-[16px] text-gray-500">
              {t("characters", { defaultValue: "Characters" })}: {description.length}/{MAX_DESCRIPTION_LENGTH}
            </p>
            {descriptionError && (
              <p className="text-right text-[12px] leading-[16px] text-red-500">{descriptionError}</p>
            )}
          </div>
        </div>

        {previewUrl && (
          <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-gray-200">
            {selectedFile?.type.startsWith("image/") ? (
              <img src={previewUrl} alt="Preview" className="h-full w-full object-cover" />
            ) : (
              <video src={previewUrl} className="h-full w-full object-cover" controls />
            )}
            <button
              onClick={clearFile}
              disabled={loading}
              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-[12px] text-white"
              type="button"
              aria-label="Remove file"
            >
              ×
            </button>
          </div>
        )}

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <label
              aria-label="Add photo"
              className="flex h-8 w-8 min-w-8 cursor-pointer items-center justify-center rounded-2xl bg-gray-100 text-blue-500 hover:bg-gray-200"
            >
              <PhotoIcon className="h-4 w-4" />
              <input
                key={`photo-${fileInputKey}`}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                disabled={loading}
              />
            </label>

            {VIDEO_UPLOAD_ENABLED && (
              <label
                aria-label="Add video"
                className="flex h-8 w-8 min-w-8 cursor-pointer items-center justify-center rounded-2xl bg-gray-100 text-blue-500 hover:bg-gray-200"
              >
                <VideoCameraIcon className="h-4 w-4" />
                <input
                  key={`video-${fileInputKey}`}
                  type="file"
                  accept="video/*"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={loading}
                />
              </label>
            )}

            <div ref={emojiBoxRef} className="relative">
              <button
                type="button"
                aria-label="Add emoji"
                disabled={loading}
                onClick={() => setShowEmojiPicker((prev) => !prev)}
                className="flex h-8 w-8 min-w-8 items-center justify-center rounded-2xl bg-gray-100 text-blue-500 hover:bg-gray-200"
              >
                <FaceSmileIcon className="h-4 w-4" />
              </button>

              {showEmojiPicker && (
                <div className="absolute bottom-full left-0 z-50 mb-2 max-w-[calc(100vw-3rem)] overflow-hidden rounded-2xl shadow-xl">
                  <EmojiPicker
                    onEmojiClick={handleEmojiClick}
                    width="100%"
                    height={350}
                    lazyLoadEmojis
                    skinTonesDisabled
                    previewConfig={{ showPreview: false }}
                  />
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`flex h-8 min-w-8 items-center justify-center rounded-2xl px-3 py-1.5 text-[14px] font-medium leading-[20px] text-white transition ${
              loading ? "cursor-not-allowed bg-gray-400" : "bg-blue-500 hover:bg-blue-600"
            }`}
          >
            {loading ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
            ) : (
              t("post")
            )}
          </button>
        </div>
      </div>
    </div>
  );
};